/**
 * MailRegisterStepper — visual progress indicator for the wizard. Active
 * step is highlighted; completed steps are clickable to navigate back.
 */
import type { JSX } from 'react';

import type { DraftStep } from '../../lib/api/types';

const STEP_ORDER: DraftStep[] = ['SENDER', 'RECEIVER', 'PACKAGE', 'PRICING', 'REVIEW'];

const STEP_LABELS: Record<DraftStep, string> = {
  SENDER: 'Sender',
  RECEIVER: 'Receiver',
  PACKAGE: 'Package',
  PRICING: 'Pricing',
  REVIEW: 'Review',
};

interface MailRegisterStepperProps {
  current: DraftStep;
  onNavigate: (step: DraftStep) => void;
}

export function MailRegisterStepper({ current, onNavigate }: MailRegisterStepperProps): JSX.Element {
  const currentIdx = STEP_ORDER.indexOf(current);
  return (
    <ol className="flex items-center gap-2" data-testid="mail-stepper">
      {STEP_ORDER.map((step, idx) => {
        const isActive = step === current;
        const isCompleted = idx < currentIdx;
        const isClickable = isCompleted;
        return (
          <li key={step} className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => isClickable && onNavigate(step)}
              disabled={!isClickable && !isActive}
              data-testid={`mail-stepper-${step.toLowerCase()}`}
              data-active={isActive ? 'true' : 'false'}
              data-completed={isCompleted ? 'true' : 'false'}
              className={`flex items-center gap-2 rounded-full px-3 py-1 text-sm font-medium transition ${
                isActive
                  ? 'bg-nipost-600 text-white'
                  : isCompleted
                    ? 'bg-nipost-100 text-nipost-700 hover:bg-nipost-200 cursor-pointer'
                    : 'bg-slate-100 text-slate-500'
              }`}
            >
              <span className="flex h-5 w-5 items-center justify-center rounded-full bg-white/30 text-xs">
                {idx + 1}
              </span>
              <span>{STEP_LABELS[step]}</span>
            </button>
            {idx < STEP_ORDER.length - 1 && (
              <span className="h-px w-6 bg-slate-300" aria-hidden="true" />
            )}
          </li>
        );
      })}
    </ol>
  );
}

export const MAIL_STEPS = STEP_ORDER;
